/**
 * THE GOVERNED PRECONSTRUCTION COMMITMENT (`precon-control-plane-1`).
 *
 * Construction software usually makes "Approve" mean an unexamined mixture of
 * accepted, committed, scheduled, issued and implemented. This contract
 * refuses that, and so does this module. Four facts stay separate:
 *
 *   AUTHORIZED   a person, holding the right role, said yes to one specific
 *                freshly-recomputed proposal
 *   RECORDED     the decision went into the audit log
 *   APPLIED      what was actually written — on this contract version,
 *                `applied` is `Literal[False]`. Nothing.
 *   DEFERRED     the writes the platform declined, each with its blocker
 *
 * The commit refuses in three governed ways, and none is a transport failure:
 * 428 wants a confirmation that has not been given; 409 `stale_proposal`
 * means the project moved under the proposal; 409 `proposal_not_ready`
 * carries the analysis's own blockers. A UI that retries any of them
 * automatically has converted a governed refusal into a bug workaround.
 *
 * The decision OUTCOME is not modelled here. F-AUTONOMY-0 already owns that
 * vocabulary and this contract's response is one of the two shapes it reads;
 * a second outcome model would be a second visual language for the same four
 * facts.
 */

export const PRECON_CONTRACT = 'precon-control-plane-1';

/** Only these four may commit. Anyone authenticated may read the analysis. */
export const PRECON_COMMIT_ROLES = ['admin', 'project_manager', 'pm', 'superintendent'];

export const canCommitPrecon = (user) => PRECON_COMMIT_ROLES.includes(user?.role);

export const AUTHORITY_NOTE =
  'Recording a preconstruction commitment is reserved for project managers, superintendents and admins. Anyone on the project may read the analysis.';

/* -------------------------------------------------------------------------
 * THE ANALYSIS — read-only, and the input to a commitment
 * ---------------------------------------------------------------------- */

const READINESS = {
  READY: {
    key: 'READY',
    label: 'Ready for a human decision',
    tone: 'ready',
    meaning: 'The analysis found nothing blocking a commitment. It is still a proposal until a person commits it.',
  },
  NOT_READY: {
    key: 'NOT_READY',
    // Never "failed": the analysis ran fine and reported what stands in the way.
    label: 'Not ready to commit',
    tone: 'blocked',
    meaning: 'The analysis names what stands in the way. Nothing here is a fault of the analysis or of anyone on the project.',
  },
};

export const readinessDisplay = (readiness) =>
  READINESS[readiness] || {
    key: String(readiness ?? 'UNKNOWN'),
    label: 'A readiness this view does not name',
    tone: 'unknown',
    meaning: 'The server named a readiness value this view does not carry. It is shown verbatim.',
  };

export const PROPOSAL_ONLY_NOTE =
  'This is a proposal. It is not the plan, it does not become the plan by default, and it does not become the plan by the passage of time.';

export const presentPreconAnalysis = (payload) => {
  if (!payload) return null;
  const schedule = payload.schedule || null;
  return {
    contractVersion: payload.contractVersion || null,
    projectId: payload.projectId || null,
    proposalId: payload.proposalId || null,
    // All three are Literal-pinned in the response schema. Carried rather
    // than assumed, so a contract change is visible instead of silent.
    proposed: payload.proposed === true,
    requiresEitlApproval: payload.requires_eitl_approval === true,
    requiresOperatorConfirmation: payload.requiresOperatorConfirmation === true,
    readiness: readinessDisplay(payload.readiness),
    readyForHumanReview: payload.readyForHumanReview === true,
    analysisDate: payload.analysisDate || null,
    proposalOnlyNote: PROPOSAL_ONLY_NOTE,
    blockers: payload.blockers || [],
    warnings: payload.warnings || [],
    missingInformation: payload.missingInformation || [],
    assumptions: payload.assumptions || [],
    operatorDecisions: payload.operatorDecisions || [],
    packages: payload.packages || [],
    packageCount: (payload.packages || []).length,
    proposedChanges: schedule ? schedule.proposedChanges || [] : [],
    criticalPath: schedule ? schedule.criticalPath || [] : [],
    hasSchedule: Boolean(schedule),
    sourceRecords: payload.sourceRecords || null,
    routing: payload.routing || null,
    // A commitment is only offered against a proposal the analysis itself
    // calls ready. Offering it otherwise invites a 409 the user cannot act on.
    committable: payload.readiness === 'READY' && Boolean(payload.proposalId),
  };
};

/* -------------------------------------------------------------------------
 * WHAT A COMMITMENT WILL AND WILL NOT DO — stated before the click
 * ---------------------------------------------------------------------- */

export const WILL_RECORD = [
  'Your approval of this specific proposal, against the proposal id it was calculated under.',
  'Your name and note, if you give them, on the audit record.',
  'The packages and critical path the analysis covered, as evidence of what you reviewed.',
];

export const WILL_NOT_DO = [
  'Change any activity or planned date.',
  'Create or alter a bid package.',
  'Write a procurement item or a release date.',
  'Post anything to the marketplace.',
  'Send anything to anyone outside this tenant.',
];

export const APPLIED_LOCKED_NOTE =
  'On this contract version the response’s applied flag is fixed at false. A commitment here records a decision; it cannot write the schedule, and the panel below will say exactly which writes were deferred.';

export const presentCommitmentPreview = (analysis) => {
  if (!analysis) return null;
  return {
    proposalId: analysis.proposalId,
    packageCount: analysis.packageCount,
    proposedChangeCount: analysis.proposedChanges.length,
    criticalPathLength: analysis.criticalPath.length,
    willRecord: WILL_RECORD,
    willNotDo: WILL_NOT_DO,
    appliedLockedNote: APPLIED_LOCKED_NOTE,
    blockers: analysis.blockers,
    committable: analysis.committable,
  };
};

/* -------------------------------------------------------------------------
 * THE THREE GOVERNED REFUSALS
 * ---------------------------------------------------------------------- */

const REFUSALS = {
  confirmation_required: {
    kind: 'CONFIRMATION_REQUIRED',
    headline: 'Confirmation required',
    // NOT an error. The backend is asking for the deliberate act it is
    // designed to require, and retrying with confirmed:true would be the UI
    // answering on the human's behalf.
    detail:
      'OMNΛS will not record a commitment without a deliberate confirmation. Nothing was recorded.',
    recoverable: true,
    autoRetry: false,
  },
  stale_proposal: {
    kind: 'STALE_PROPOSAL',
    headline: 'The project changed since this proposal was calculated',
    detail:
      'This proposal was computed against an earlier state of the project. Re-run the analysis and review the current one before committing. Nothing was recorded.',
    recoverable: true,
    autoRetry: false,
  },
  proposal_not_ready: {
    kind: 'NOT_READY',
    headline: 'Not ready to commit',
    detail: 'OMNΛS cannot accept this commitment yet. Nothing was recorded.',
    recoverable: true,
    autoRetry: false,
  },
};

export const NEVER_AUTO_RETRY_NOTE =
  'Each of these is a governed refusal, not a transport failure. None of them is retried automatically, and none of them recorded anything.';

/**
 * Classify a commit refusal.
 *
 * `autoRetry` is `false` on every branch and a test asserts it: a UI that
 * re-sends with `confirmed: true` after a 428 has answered on the human's
 * behalf, and one that re-sends after a stale 409 has committed against a
 * project state nobody reviewed.
 */
const ANALYSIS_FAILURES = {
  disabled: 'The preconstruction control plane is not enabled in this environment.',
  'not-entitled': 'The preconstruction control plane is not part of your plan.',
  'no-access': 'Your role does not include reading the preconstruction analysis.',
  'not-found': 'This project could not be read.',
};

export const ANALYSIS_FAILED_DETAIL =
  'Nothing was sent and nothing was recorded. Run the analysis again when you are ready.';

/**
 * An analysis that could not be read is not a commitment that was refused.
 * Reusing the commit copy here would tell a reader a commitment failed when
 * none was ever attempted.
 */
export const presentAnalysisFailure = (error) => {
  if (!error) return null;
  return {
    kind: error.kind || 'ERROR',
    headline: ANALYSIS_FAILURES[error.kind] || 'The preconstruction analysis did not load.',
    detail: ANALYSIS_FAILED_DETAIL,
    retryable: !['disabled', 'not-entitled', 'no-access', 'not-found'].includes(error.kind),
  };
};

export const presentCommitRefusal = (error) => {
  if (!error) return null;
  const code = error.code || error.detail?.code || null;
  const known = code ? REFUSALS[code] : null;
  if (known) {
    return {
      ...known,
      code,
      // The server's own message and its structured payload, carried rather
      // than replaced: `blockers` and `currentProposalId` are the whole point.
      message: error.message || null,
      blockers: error.blockers || [],
      currentProposalId: error.currentProposalId || null,
      neverAutoRetry: NEVER_AUTO_RETRY_NOTE,
    };
  }
  switch (error.kind) {
    case 'disabled':
      return {
        kind: 'DISABLED',
        headline: 'The preconstruction control plane is not enabled in this environment.',
        detail: null,
        recoverable: false,
        autoRetry: false,
        blockers: [],
      };
    case 'no-access':
      return {
        kind: 'NO_ACCESS',
        headline: 'Your role does not include recording a preconstruction commitment.',
        detail: AUTHORITY_NOTE,
        recoverable: false,
        autoRetry: false,
        blockers: [],
      };
    case 'not-found':
      return {
        kind: 'NOT_FOUND',
        headline: 'This project could not be read.',
        detail: null,
        recoverable: false,
        autoRetry: false,
        blockers: [],
      };
    default:
      return {
        kind: 'ERROR',
        headline: 'The commitment could not be sent.',
        detail: 'Nothing was recorded. The analysis above is unaffected.',
        recoverable: true,
        autoRetry: false,
        blockers: [],
      };
  }
};

/**
 * Pull the coded refusal out of an axios error without losing its payload.
 *
 * The three governed refusals live in `detail` as an object; a plain-string
 * detail is something else entirely and must not be mistaken for one.
 */
export const readCommitError = (error) => {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object') {
    return {
      code: detail.code || null,
      message: detail.message || null,
      blockers: detail.blockers || [],
      currentProposalId: detail.currentProposalId || null,
      status: error?.response?.status ?? null,
    };
  }
  return null;
};

/* -------------------------------------------------------------------------
 * THE RECALCULATION SUMMARY — the applied-flag repair
 *
 * Two screens asserted "No schedule changes were applied" from a hardcoded
 * string. The summary carries no `applied` key at all; what it carries is
 * `approvedBaselineChanged`. And recalculation DOES write — readiness states,
 * ERS, audit events — so a flat "no changes were applied" misdescribes an
 * operation that evaluated instances and wrote their readiness.
 * ---------------------------------------------------------------------- */

export const BASELINE_UNCHANGED = 'The approved baseline did not change.';
export const BASELINE_CHANGED = 'The approved baseline changed.';
export const BASELINE_NOT_STATED =
  'The server did not state whether the approved baseline changed.';

export const presentRecalculationSummary = (summary) => {
  if (!summary) return null;
  const stated = Object.prototype.hasOwnProperty.call(summary, 'approvedBaselineChanged');
  const changed = summary.approvedBaselineChanged === true;
  const evaluated = Object.prototype.hasOwnProperty.call(summary, 'instancesEvaluated')
    ? summary.instancesEvaluated
    : null;
  return {
    skipped: summary.skipped === true,
    skipReason: summary.reason || null,
    instancesEvaluated: evaluated,
    realism: summary.realism || null,
    canFinishOnTime: summary.canFinishOnTime || 'UNKNOWN',
    baselineStated: stated,
    baselineChanged: stated ? changed : null,
    // Read from the payload, never asserted from a constant. An absent key
    // is neither a yes nor a no.
    baselineSentence: !stated ? BASELINE_NOT_STATED : changed ? BASELINE_CHANGED : BASELINE_UNCHANGED,
    // What the run DID write. Saying only what did not change would
    // misdescribe an operation that re-evaluated instances and stored their
    // readiness.
    wroteSentence:
      evaluated === null
        ? 'The server did not state how many instances were evaluated.'
        : `${evaluated} instance${evaluated === 1 ? '' : 's'} re-evaluated; readiness, earliest realistic start and audit events were written.`,
  };
};

/** One line for a toast, composed from the payload rather than asserted. */
export const recalculationSentence = (summary) => {
  const view = presentRecalculationSummary(summary);
  if (!view) return null;
  if (view.skipped) return view.skipReason || 'Recalculation was skipped.';
  return `${view.wroteSentence} Realism: ${view.realism || '—'} · can finish on time: ${view.canFinishOnTime}. ${view.baselineSentence}`;
};

/* -------------------------------------------------------------------------
 * COPY, AND THE GUARD THAT WALKS IT
 * ---------------------------------------------------------------------- */

export const PRECON_COPY = {
  sectionTitle: 'Preconstruction commitment',
  sectionLead:
    'OMNΛS analyses the project and proposes a preconstruction position. Committing records your decision on the record — it is a governed act, and it is not the same as the schedule moving.',
  analyzeAction: 'Run preconstruction analysis',
  reviewTitle: 'What you are authorizing',
  willRecordTitle: 'What OMNΛS will record',
  willNotTitle: 'What OMNΛS will not do',
  blockersTitle: 'What stands in the way',
  commitAction: 'Commit preconstruction decision',
  confirmTitle: 'Confirm commitment',
  confirmBody:
    'This records your governed preconstruction decision. It does not mean any proposed schedule or downstream write will be applied. OMNΛS will report exactly what it recorded and what it deferred.',
  confirmAction: 'Confirm',
  cancelAction: 'Cancel',
  nameLabel: 'Your name for the record',
  noteLabel: 'Note (optional)',
  refreshAction: 'Re-run analysis',
};

/**
 * Words that would collapse the four facts this surface exists to separate.
 * Server text is echoed verbatim and excluded — we do not rewrite the
 * server's words, we only never add our own.
 */
export const FORBIDDEN_PRECON_WORDS = {
  collapse: ['executed', 'implemented', 'issued', 'scheduled', 'done', 'complete', 'success'],
  overclaim: ['applied the schedule', 'plan updated', 'dates moved', 'baseline updated'],
};

export const authoredPreconCopy = () => [
  ...Object.values(PRECON_COPY),
  ...Object.values(READINESS).flatMap((entry) => [entry.label, entry.meaning]),
  ...Object.values(REFUSALS).flatMap((entry) => [entry.headline, entry.detail]),
  ...WILL_RECORD,
  ...WILL_NOT_DO,
  ...Object.values(ANALYSIS_FAILURES),
  ANALYSIS_FAILED_DETAIL,
  AUTHORITY_NOTE,
  PROPOSAL_ONLY_NOTE,
  APPLIED_LOCKED_NOTE,
  NEVER_AUTO_RETRY_NOTE,
  BASELINE_UNCHANGED,
  BASELINE_CHANGED,
  BASELINE_NOT_STATED,
];

export const findForbiddenPreconWords = (text) => {
  if (typeof text !== 'string') return [];
  const hits = [];
  Object.values(FORBIDDEN_PRECON_WORDS).forEach((group) => {
    group.forEach((word) => {
      const pattern = new RegExp(`\\b${word.replace(/ /g, '\\s+')}\\b`, 'i');
      if (pattern.test(text)) hits.push(word);
    });
  });
  return hits;
};
